import type { FormerPractitionerLicenseDto } from '@supply-mover-2-0/shared';
import type { FieldConfiguration, ListBuilderConfiguration } from '../types';

/**
 * PD-17483 — the account's Former practitioners, shown collapsed on the client
 * Licenses tab. On a human practice, a practitioner's lapsed, retired,
 * revoked, surrendered or deceased license that a colleague's same-kind
 * license covers is left off the Compliance Dead License card (Chris,
 * 2026-10-24); these are exactly those licenses.
 *
 * Data source: `GET /api/licenses/clients/:clientId/former-practitioners`
 * (FormerPractitionerLicenseDto). The set is small by construction (one
 * account's practitioner licenses), so the endpoint returns it whole, in its
 * own order (latest expiration first), and the columns do not sort. The tab
 * passes `showConfigButton={false}`: the rows carry only the fields below, so
 * a column added in the configurator would have nothing to read.
 *
 * The board's status word is shown verbatim (`boardStatus`), never the
 * canonical bucket, and a row the matchmaker only suggested says so.
 */

const textField = (
	id: string,
	fieldName: string,
	options: { secondary?: boolean; weight?: 'medium' } = {},
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName }, defaultValue: '-' },
	displayStyle: 'text',
	styleConfig: {
		size: options.secondary === true ? 'xs' : 'sm',
		weight: options.weight,
		colorRule:
			options.secondary === true
				? { type: 'theme', themeColor: 'textSecondary' }
				: { type: 'default' },
	},
});

const dateField = (id: string, fieldName: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName }, defaultValue: '-' },
	displayStyle: 'date',
	styleConfig: {
		size: 'sm',
		colorRule: { type: 'default' },
		// Licensing reads four-digit years (formatDateSimple 'mdy').
		formatOptions: { dateFormat: 'mdy' },
	},
});

export const CLIENT_FORMER_PRACTITIONERS_LIST_KEY = 'client_former_practitioners';
export const CLIENT_FORMER_PRACTITIONERS_LIST_ID = 'default-client-former-practitioners';

export const CLIENT_FORMER_PRACTITIONERS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: CLIENT_FORMER_PRACTITIONERS_LIST_ID,
			cacheTTL: 300000,
			name: 'Former Practitioners',
			type: 'default',
			active: true,
			sortOrder: 0,
			searchEnabled: false,
			columns: [
				{
					id: 'former-practitioner-registrant',
					fields: [
						textField('registrant-field', 'practitionerName', { weight: 'medium' }),
						{
							id: 'registrant-lb',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						textField('license-label-field', 'licenseLabel', { secondary: true }),
					],
					headers: [
						{ id: 'h-registrant', text: 'Registrant' },
						{ id: 'h-registrant-gap', text: '' },
						{ id: 'h-license', text: 'License' },
					],
					sortable: false,
					widthConfig: { mode: 'flexible', preset: 'large' },
				},
				{
					id: 'former-practitioner-board-status',
					fields: [textField('board-status-field', 'boardStatus')],
					headers: [{ id: 'h-board-status', text: 'Board Status' }],
					sortable: false,
					width: 160,
				},
				{
					id: 'former-practitioner-status-changed',
					fields: [dateField('status-changed-field', 'statusChangedAt')],
					headers: [{ id: 'h-status-changed', text: 'Status Changed' }],
					sortable: false,
					width: 140,
				},
				{
					id: 'former-practitioner-expires',
					fields: [dateField('expires-field', 'expirationDate')],
					headers: [{ id: 'h-expires', text: 'Expires' }],
					sortable: false,
					width: 120,
				},
				{
					id: 'former-practitioner-match',
					fields: [
						{
							// Only a suggested match carries the pill; a confirmed one
							// is the ordinary case and needs none.
							id: 'suggested-only-pill',
							dataConfig: { type: 'custom_text', customText: 'Suggested only' },
							displayStyle: 'badge',
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'static', staticColor: { type: 'warning' } },
							},
							conditionalVisibility: {
								field: 'matchState',
								condition: 'equals',
								value: 'suggested',
							},
						},
					],
					headers: [{ id: 'h-match', text: 'Match' }],
					sortable: false,
					width: 140,
				},
			],
			clickAction: {
				type: 'navigate',
				path: '/licenses/{id}',
			},
			actions: [],
		},
	],
	paging: { defaultPageSize: 10, pageSizeOptions: [10, 25] },
	filters: { activeFilters: [], defaultFilters: {} },
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: 'practitionerName', fieldLabel: 'Registrant' },
				{ fieldName: 'licenseLabel', fieldLabel: 'License' },
				{ fieldName: 'boardStatus', fieldLabel: 'Board Status' },
				{ fieldName: 'statusChangedAt', fieldLabel: 'Status Changed' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expires' },
				{ fieldName: 'matchState', fieldLabel: 'Match' },
			],
		},
	],
};

/** A row as the list reads it: the endpoint's row, plus the row id and the license line. */
export interface FormerPractitionerListRowModel extends FormerPractitionerLicenseDto {
	/** The client license id: the row key, and where a click goes (`/licenses/{id}`). */
	id: number;
	/** "DEA AS2222222 · IL": the license type and number as filed, and the issuing state. */
	licenseLabel: string;
}

/** Shape the endpoint's rows for the list. */
export function formerPractitionerListRows(
	rows: ReadonlyArray<FormerPractitionerLicenseDto>,
): Array<FormerPractitionerListRowModel> {
	return rows.map((row) => {
		const license = [row.licenseType, row.licenseNumber]
			.map((part) => part?.trim() ?? '')
			.filter((part) => part !== '')
			.join(' ');
		const state = row.stateCode?.trim() ?? '';
		return {
			...row,
			id: row.licenseId,
			licenseLabel: [license, state].filter((part) => part !== '').join(' · '),
		};
	});
}
